// MapCanvas's sources and layers as plain style JSON (types only from MapLibre, so it stays unit-testable). Order,
// bottom to top: territory fills, outlines and labels; selection halos; store clusters and pins; the selected-pin
// ring; rep markers. Pins mirror iOS PinMarker: a colored dot with a white ring and its label (priority letter or
// visit count) inside.

import type { ExpressionSpecification } from '@maplibre/maplibre-gl-style-spec';
import type { GeoJSONSourceSpecification, LayerSpecification } from 'maplibre-gl';
import {
  CLUSTER_MAX_ZOOM,
  CLUSTER_RADIUS,
  CLUSTER_RADIUS_EXPRESSION,
  clusterColorExpression,
  clusterColorProperties,
  clusterInkExpression,
  type JsonExpression,
  type MarkerCollection,
  type PointCollection,
  type PolygonCollection,
  type PolygonLabelCollection,
} from './mapData';

/** OpenFreeMap vector styles (no key, no quota; WEB_PLAN.md §1). Dark falls back to Liberty if it ever fails to load. */
export const MAP_STYLE_LIGHT = 'https://tiles.openfreemap.org/styles/liberty';
export const MAP_STYLE_DARK = 'https://tiles.openfreemap.org/styles/dark';

/** Fonts the OpenFreeMap glyph server has (the Liberty and Dark styles use the same Noto Sans stack). */
const FONT_BOLD = ['Noto Sans Bold'];

export const SOURCE = {
  points: 'gt-src-points',
  polygons: 'gt-src-polygons',
  polygonLabels: 'gt-src-polygon-labels',
  markers: 'gt-src-markers',
  highlights: 'gt-src-highlights',
} as const;

export const LAYER = {
  polygonFill: 'gt-polygon-fill',
  polygonLine: 'gt-polygon-line',
  polygonLabel: 'gt-polygon-label',
  highlights: 'gt-point-highlights',
  clusters: 'gt-clusters',
  clusterCount: 'gt-cluster-count',
  points: 'gt-points',
  pointLabels: 'gt-point-labels',
  selected: 'gt-point-selected',
  markers: 'gt-markers',
  markerLabels: 'gt-marker-labels',
} as const;

/** Layers that belong to the (rebuildable) store-pin source. */
export const POINT_LAYER_IDS: readonly string[] = [LAYER.clusters, LAYER.clusterCount, LAYER.points, LAYER.pointLabels, LAYER.selected];

export interface MapTheme {
  dark: boolean;
}

/** Text and halo for labels drawn over the base map (territory names). */
function labelColors(theme: MapTheme): { text: string; halo: string } {
  return theme.dark ? { text: '#EEF2FA', halo: '#0B0F17' } : { text: '#102A5E', halo: '#FFFFFF' };
}

/** Our loosely typed JSON expressions are valid style expressions; this is the one place that says so to TypeScript. */
function expr(e: JsonExpression | string): ExpressionSpecification {
  return e as unknown as ExpressionSpecification;
}

const IS_CLUSTER: ExpressionSpecification = ['has', 'point_count'];
const NOT_CLUSTER: ExpressionSpecification = ['!', ['has', 'point_count']];

/** The store-pin source: clustered (with per-color counters) or plain. */
export function pointSource(data: PointCollection, options: { cluster: boolean; palette: readonly string[] }): GeoJSONSourceSpecification {
  if (!options.cluster) return { type: 'geojson', data };
  return {
    type: 'geojson',
    data,
    cluster: true,
    clusterRadius: CLUSTER_RADIUS,
    clusterMaxZoom: CLUSTER_MAX_ZOOM,
    clusterProperties: clusterColorProperties(options.palette) as unknown as GeoJSONSourceSpecification['clusterProperties'],
  };
}

export function polygonSource(data: PolygonCollection): GeoJSONSourceSpecification {
  return { type: 'geojson', data };
}

export function polygonLabelSource(data: PolygonLabelCollection): GeoJSONSourceSpecification {
  return { type: 'geojson', data };
}

export function markerSource(data: MarkerCollection): GeoJSONSourceSpecification {
  return { type: 'geojson', data };
}

/** The selection halos: the selected pins again, never clustered, so every selected store shows at any zoom. */
export function highlightSource(data: PointCollection): GeoJSONSourceSpecification {
  return { type: 'geojson', data };
}

/**
 * The filter for the selected-pin ring: never a cluster, ids compared case-insensitively (payload ids are upper-case,
 * envelope ids lower-case), and no selection matches nothing.
 */
export function selectedFilter(selectedId: string | null | undefined): ExpressionSpecification {
  if (!selectedId) return ['boolean', false];
  return ['all', NOT_CLUSTER, ['==', ['downcase', ['get', 'id']], selectedId.toLowerCase()]];
}

/** Territory fills (0.18 opacity, as iOS), outlines and centroid labels. */
export function polygonLayers(theme: MapTheme): LayerSpecification[] {
  const label = labelColors(theme);
  return [
    {
      id: LAYER.polygonFill,
      type: 'fill',
      source: SOURCE.polygons,
      paint: { 'fill-color': ['get', 'color'], 'fill-opacity': 0.18 },
    },
    {
      id: LAYER.polygonLine,
      type: 'line',
      source: SOURCE.polygons,
      layout: { 'line-join': 'round' },
      paint: { 'line-color': ['get', 'color'], 'line-width': 2, 'line-opacity': 0.9 },
    },
    {
      id: LAYER.polygonLabel,
      type: 'symbol',
      source: SOURCE.polygonLabels,
      layout: {
        'text-field': ['get', 'name'],
        'text-font': FONT_BOLD,
        'text-size': 12,
        'text-max-width': 10,
        'text-allow-overlap': false,
      },
      paint: { 'text-color': label.text, 'text-halo-color': label.halo, 'text-halo-width': 1.5 },
    },
  ];
}

/**
 * Store pins: cluster bubbles with counts, single pins with labels, and the selected-pin ring. Pins keep the iOS white
 * ring in both themes (it separates touching pins on any base map), so the theme only matters for the selection ring.
 */
export function pointLayers(
  theme: MapTheme,
  options: { cluster: boolean; palette: readonly string[]; selectedId?: string | null | undefined },
): LayerSpecification[] {
  const layers: LayerSpecification[] = [];
  if (options.cluster) {
    layers.push(
      {
        id: LAYER.clusters,
        type: 'circle',
        source: SOURCE.points,
        filter: IS_CLUSTER,
        paint: {
          'circle-color': expr(clusterColorExpression(options.palette)),
          'circle-radius': expr(CLUSTER_RADIUS_EXPRESSION),
          'circle-stroke-width': 2.5,
          'circle-stroke-color': '#FFFFFF',
          'circle-opacity': 0.95,
        },
      },
      {
        id: LAYER.clusterCount,
        type: 'symbol',
        source: SOURCE.points,
        filter: IS_CLUSTER,
        layout: {
          'text-field': ['get', 'point_count_abbreviated'],
          'text-font': FONT_BOLD,
          'text-size': 12,
          'text-allow-overlap': true,
          'text-ignore-placement': true,
        },
        paint: { 'text-color': expr(clusterInkExpression(options.palette)) },
      },
    );
  }
  layers.push(
    {
      id: LAYER.points,
      type: 'circle',
      source: SOURCE.points,
      filter: NOT_CLUSTER,
      paint: {
        'circle-color': ['get', 'color'],
        // A touch bigger when zoomed in, where pins are spread out and easier to aim at.
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 8, 6, 14, 9, 18, 11],
        'circle-stroke-width': 2,
        'circle-stroke-color': '#FFFFFF',
      },
    },
    {
      id: LAYER.pointLabels,
      type: 'symbol',
      source: SOURCE.points,
      filter: ['all', NOT_CLUSTER, ['!=', ['get', 'label'], '']],
      minzoom: 11,
      layout: {
        'text-field': ['get', 'label'],
        'text-font': FONT_BOLD,
        'text-size': 10,
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
      paint: { 'text-color': ['get', 'ink'] },
    },
    {
      id: LAYER.selected,
      type: 'circle',
      source: SOURCE.points,
      filter: selectedFilter(options.selectedId),
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 8, 11, 14, 14, 18, 16],
        'circle-color': 'rgba(0,0,0,0)',
        'circle-stroke-width': 3,
        'circle-stroke-color': theme.dark ? '#FF8C6B' : '#F96A3C',
      },
    },
  );
  return layers;
}

/**
 * Selection halos (map multi-select): a blue disc a little larger than the pin, drawn under the pins and clusters so
 * a selected pin reads as pin, white ring, blue ring, and a zoomed-out selection shows as a blue wash around the
 * cluster bubbles without covering their counts. Blue, so it never reads as the orange peek ring.
 */
export function highlightLayers(theme: MapTheme): LayerSpecification[] {
  return [
    {
      id: LAYER.highlights,
      type: 'circle',
      source: SOURCE.highlights,
      paint: {
        'circle-color': theme.dark ? '#5B9BFF' : '#2673E6',
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 4, 5, 8, 10, 14, 14, 18, 16],
        'circle-opacity': 0.9,
        'circle-stroke-width': 1,
        'circle-stroke-color': theme.dark ? '#0B0F17' : '#FFFFFF',
      },
    },
  ];
}

/** Rep markers: presence-colored discs with initials, above everything else. */
export function markerLayers(theme: MapTheme): LayerSpecification[] {
  return [
    {
      id: LAYER.markers,
      type: 'circle',
      source: SOURCE.markers,
      paint: {
        'circle-color': ['get', 'color'],
        'circle-radius': 12,
        'circle-stroke-width': 3,
        'circle-stroke-color': theme.dark ? '#EEF2FA' : '#102A5E',
      },
    },
    {
      id: LAYER.markerLabels,
      type: 'symbol',
      source: SOURCE.markers,
      filter: ['!=', ['get', 'label'], ''],
      layout: {
        'text-field': ['get', 'label'],
        'text-font': FONT_BOLD,
        'text-size': 10,
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
      paint: { 'text-color': ['get', 'ink'] },
    },
  ];
}
